import type {Component, Editor} from 'grapesjs';

export interface TextCharCounterConfig {
  componentType: string;
  maxChars: number;
  counterClass: string;
  counterAtMaxClass: string;
  counterVisibleClass: string;
  charCountText: string;
  counterGapPx?: number;
  listenersBoundFlag: string;
}

const DEFAULT_COUNTER_GAP_PX = 4;

const INSERT_INPUT_TYPES = new Set<string>([
  'insertText',
  'insertCompositionText',
  'insertFromComposition',
  'insertReplacementText',
  'insertFromPaste',
  'insertFromDrop',
  'insertLineBreak',
  'insertParagraph',
]);

interface ControllerHandle {
  editor: Editor;
  config: TextCharCounterConfig;
  trackedEl: HTMLElement | null;
  positionRafHandle: number | null;
  resizeObserver: ResizeObserver | null;
  counterEl: HTMLDivElement | null;
  lastRenderedLength: number;
}

const handlesByEditor = new WeakMap<Editor, ControllerHandle[]>();

export function initTextCharCounter(editor: Editor, config: TextCharCounterConfig): void {
  const existing = handlesByEditor.get(editor) ?? [];
  if (existing.some((h) => h.config.componentType === config.componentType)) {
    return;
  }
  const handle: ControllerHandle = {
    editor,
    config,
    trackedEl: null,
    positionRafHandle: null,
    resizeObserver: null,
    counterEl: null,
    lastRenderedLength: -1,
  };
  existing.push(handle);
  handlesByEditor.set(editor, existing);

  editor.on('component:selected', (component: Component) => {
    if (getComponentType(component) !== config.componentType) {
      hideCounter(handle);
      return;
    }
    const el = component.getEl();
    if (!el) {
      return;
    }
    handle.trackedEl = el;
    ensureDocumentListeners(handle);
    observeLayoutChanges(handle);
    showCounter(handle);
  });
  editor.on('component:deselected', () => {
    hideCounter(handle);
  });
}

function getTextLength(el: HTMLElement): number {
  return el.textContent?.length ?? 0;
}

function enforceMaxLength(el: HTMLElement, maxChars: number): void {
  const text = el.textContent ?? '';
  if (text.length <= maxChars) {
    return;
  }
  let trimmed = text.slice(0, maxChars);
  const lastCode = trimmed.charCodeAt(trimmed.length - 1);
  if (lastCode >= 0xd800 && lastCode <= 0xdbff) {
    trimmed = trimmed.slice(0, -1);
  }
  el.textContent = trimmed;
  const range = el.ownerDocument.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const selection = el.ownerDocument.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function getCanvasBody(handle: ControllerHandle): HTMLElement | null {
  return handle.editor.Canvas.getBody() ?? null;
}

function ensureDocumentListeners(handle: ControllerHandle): void {
  const doc = getCanvasBody(handle)?.ownerDocument;
  if (!doc) {
    return;
  }
  const flagged = doc as Document & Record<string, unknown>;
  if (flagged[handle.config.listenersBoundFlag] === true) {
    return;
  }
  flagged[handle.config.listenersBoundFlag] = true;
  doc.addEventListener(
    'input',
    () => {
      if (handle.trackedEl) {
        enforceMaxLength(handle.trackedEl, handle.config.maxChars);
        refreshCounter(handle);
      }
    },
    true
  );
  doc.addEventListener(
    'beforeinput',
    (event: InputEvent) => {
      const el = handle.trackedEl;
      if (el && shouldBlockBeforeInput(el, event, handle.config.maxChars)) {
        event.preventDefault();
      }
    },
    true
  );
  doc.addEventListener(
    'keydown',
    (event: KeyboardEvent) => {
      const el = handle.trackedEl;
      if (el && shouldBlockKey(el, event, handle.config.maxChars)) {
        event.preventDefault();
      }
    },
    true
  );
  doc.addEventListener(
    'paste',
    (event: ClipboardEvent) => {
      const el = handle.trackedEl;
      if (el) {
        handlePaste(el, event, handle.config.maxChars);
      }
    },
    true
  );
  doc.defaultView?.addEventListener(
    'scroll',
    () => {
      schedulePositioning(handle);
    },
    true
  );
}

function observeLayoutChanges(handle: ControllerHandle): void {
  if (!handle.trackedEl) {
    return;
  }
  handle.resizeObserver?.disconnect();
  const resizeObserverCtor = handle.trackedEl.ownerDocument.defaultView?.ResizeObserver ?? window.ResizeObserver;
  handle.resizeObserver = new resizeObserverCtor(() => {
    schedulePositioning(handle);
  });
  handle.resizeObserver.observe(handle.trackedEl);
  const body = getCanvasBody(handle);
  if (body) {
    handle.resizeObserver.observe(body);
  }
}

function ensureCounterEl(handle: ControllerHandle): HTMLDivElement | null {
  if (handle.counterEl?.isConnected) {
    return handle.counterEl;
  }
  const body = getCanvasBody(handle);
  if (!body) {
    return null;
  }
  const existing = body.querySelector<HTMLDivElement>(`:scope > .${handle.config.counterClass}`);
  if (existing) {
    handle.counterEl = existing;
    return existing;
  }
  const node = body.ownerDocument.createElement('div');
  node.className = handle.config.counterClass;
  node.setAttribute('contenteditable', 'false');
  body.appendChild(node);
  handle.counterEl = node;
  return node;
}

function showCounter(handle: ControllerHandle): void {
  const node = ensureCounterEl(handle);
  if (!node) {
    return;
  }
  node.classList.add(handle.config.counterVisibleClass);
  refreshCounter(handle);
}

function hideCounter(handle: ControllerHandle): void {
  handle.trackedEl = null;
  handle.resizeObserver?.disconnect();
  handle.resizeObserver = null;
  handle.lastRenderedLength = -1;
  handle.counterEl?.classList.remove(handle.config.counterVisibleClass);
}

function refreshCounter(handle: ControllerHandle): void {
  const node = ensureCounterEl(handle);
  if (!node || !handle.trackedEl) {
    return;
  }
  const length = getTextLength(handle.trackedEl);
  if (length === handle.lastRenderedLength) {
    return;
  }
  handle.lastRenderedLength = length;
  node.textContent = window.i18next.t(handle.config.charCountText, {count: length, max: handle.config.maxChars});
  node.classList.toggle(handle.config.counterAtMaxClass, length >= handle.config.maxChars);
  schedulePositioning(handle);
}

function schedulePositioning(handle: ControllerHandle): void {
  if (handle.positionRafHandle !== null || !handle.trackedEl) {
    return;
  }
  const view = getCanvasBody(handle)?.ownerDocument.defaultView ?? window;
  handle.positionRafHandle = view.requestAnimationFrame(() => {
    handle.positionRafHandle = null;
    positionCounter(handle);
  });
}

function positionCounter(handle: ControllerHandle): void {
  const node = ensureCounterEl(handle);
  const trackedEl = handle.trackedEl;
  const body = getCanvasBody(handle);
  if (!node || !trackedEl || !body) {
    return;
  }
  const gap = handle.config.counterGapPx ?? DEFAULT_COUNTER_GAP_PX;
  const target = trackedEl.getBoundingClientRect();
  const root = body.getBoundingClientRect();
  node.style.top = `${target.top - root.top - node.offsetHeight - gap}px`;
  node.style.right = `${root.right - target.right}px`;
}

function getComponentType(component: Component): string {
  const type = component.get('type');
  return typeof type === 'string' ? type : '';
}

function shouldBlockBeforeInput(el: HTMLElement, event: InputEvent, maxChars: number): boolean {
  if (!INSERT_INPUT_TYPES.has(event.inputType)) {
    return false;
  }
  if (hasSelectedTextWithin(el)) {
    return false;
  }
  const incomingLength = event.data?.length ?? 1;
  return getTextLength(el) + incomingLength > maxChars;
}

function shouldBlockKey(el: HTMLElement, event: KeyboardEvent, maxChars: number): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) {
    return false;
  }
  if (Array.from(event.key).length !== 1) {
    return false;
  }
  if (hasSelectedTextWithin(el)) {
    return false;
  }
  return getTextLength(el) + event.key.length > maxChars;
}

function hasSelectedTextWithin(el: HTMLElement): boolean {
  const selection = el.ownerDocument.getSelection();
  if (!selection || selection.isCollapsed) {
    return false;
  }
  return selection.anchorNode !== null && el.contains(selection.anchorNode);
}

function handlePaste(el: HTMLElement, event: ClipboardEvent, maxChars: number): void {
  event.preventDefault();
  event.stopPropagation();
  const pasted = event.clipboardData?.getData('text/plain') ?? '';
  if (!pasted) {
    return;
  }
  const sel = el.ownerDocument.getSelection();
  const selectedLength = sel?.rangeCount && el.contains(sel.anchorNode) && el.contains(sel.focusNode) ? sel.toString().length : 0;
  const remaining = maxChars - (getTextLength(el) - selectedLength);
  if (remaining <= 0) {
    return;
  }
  insertPlainTextIntoTrackedEl(el, pasted.slice(0, remaining));
}

function insertPlainTextIntoTrackedEl(el: HTMLElement, text: string): void {
  const ownerDocument = el.ownerDocument;
  normalizeEmpty(el);
  const selection = ownerDocument.getSelection();
  const range = getInsertionRange(el, selection);
  range.deleteContents();
  const textNode = ownerDocument.createTextNode(text);
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.collapse(true);
  selection?.removeAllRanges();
  selection?.addRange(range);
  el.normalize();
  el.dispatchEvent(new Event('input', {bubbles: true}));
}

function normalizeEmpty(el: HTMLElement): void {
  const onlyBr = el.childNodes.length === 1 && el.firstChild instanceof HTMLBRElement;
  if (onlyBr || el.childNodes.length === 0) {
    el.textContent = '';
  }
}

function getInsertionRange(el: HTMLElement, selection: Selection | null): Range {
  if (selection && selection.rangeCount > 0) {
    const existing = selection.getRangeAt(0);
    if (el.contains(existing.startContainer)) {
      return existing;
    }
  }
  const range = el.ownerDocument.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  return range;
}
